<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { EllipsisVerticalIcon, UserPlusIcon } from '@heroicons/vue/20/solid'
import { useMutation, useQuery } from '@vue/apollo-composable'
import * as I from '@/components/projects/interfaces.ts'
import { FindProjectsQuery } from '@/components/projects/graphql/project.queries.ts'
import dayjs from 'dayjs'
import { ProjectStatus } from '@/enums.ts'
import router from '@/router.ts'
import * as ProjectMutation from '@/components/projects/graphql/project.mutations.ts'
import { isNull, isUndefined, omit, sortBy } from 'lodash'
import { ChangeProjectStateType } from '@/components/projects/projects.const.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { computed, nextTick, watch } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle.ts'
import { useToast } from 'vue-toastification'
import CrdWrapper from '@/components/common/CrdWrapper.vue'
import CrdBlock from '@/components/common/CrdBlock.vue'
import { useUserStore } from '@/stores/user.store.ts'
import { storeToRefs } from 'pinia'
import type { User } from '@/components/users/interfaces.ts'

const { t } = useTranslations()
const userStore = useUserStore()
const toast = useToast()
const { projectSelected } = storeToRefs(userStore)
const {
  result: data,
  loading: isLoading,
  error,
} = useQuery<{ projects: I.Project[] }>(FindProjectsQuery, null, {
  fetchPolicy: 'cache-and-network',
})
const projects = computed<I.Project[]>(() => {
  return sortBy(data.value?.projects ?? [], ({ id }) => {
    return id === projectSelected.value?.id ? -1 : 1
  })
})

function getProjectStatus(project: I.Project): ProjectStatus {
  if (!isNull(project.archivedBy)) return ProjectStatus.Archived
  if (!isNull(project.pausedBy)) return ProjectStatus.Paused

  return ProjectStatus.Active
}

function getProjectBadgeLabelByStatus(status: ProjectStatus): string {
  return (
    {
      [ProjectStatus.Active]: 'Active',
      [ProjectStatus.Paused]: 'Paused',
      [ProjectStatus.Archived]: 'Archived',
    }[status] ?? ''
  )
}

function getProjectBadgeStylesByStatus(status: ProjectStatus): string {
  return (
    {
      [ProjectStatus.Active]: 'text-green-700 bg-green-50 ring-green-600/20',
      [ProjectStatus.Paused]: 'text-red-700 bg-red-50 ring-green-600/20',
      [ProjectStatus.Archived]: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
    }[status] ?? ''
  )
}
function formatDateToDDMMYYYYHHmm(date: string) {
  return dayjs(date).format('DD MMM YYYY HH:mm')
}

function onAddProjectsButtonClicked() {
  router.push({ path: `/projects/add` })
}

function changeProjectState(projectId: number, state: ChangeProjectStateType): void {
  const mutation =
    {
      [ChangeProjectStateType.Archive]: ProjectMutation.ArchiveProjectMutation,
      [ChangeProjectStateType.UnAarchive]: ProjectMutation.UnArchiveProjectMutation,
      [ChangeProjectStateType.Activate]: ProjectMutation.ActivateProjectMutation,
      [ChangeProjectStateType.Deactivate]: ProjectMutation.DeactivateProjectMutation,
    }[state] ?? null

  if (isNull(mutation)) {
    throw new Error('Unknown mutation')
  }

  const { onDone, onError, mutate } = useMutation<{ project: I.Project }>(mutation, () => ({
    variables: { id: projectId },
  }))

  mutate()

  onDone((response) => {
    const { project } = response.data as { project: I.Project }

    toast.success(
      {
        [ChangeProjectStateType.Archive]: 'Project «{{PROJECT_NAME}}» archived',
        [ChangeProjectStateType.UnAarchive]: 'Project «{{PROJECT_NAME}}» unarchived',
        [ChangeProjectStateType.Activate]: 'Project «{{PROJECT_NAME}}» activated',
        [ChangeProjectStateType.Deactivate]: 'Project  «{{PROJECT_NAME}}» deactivated',
      }[state].replace('{{PROJECT_NAME}}', project.title),
    )
    // TODO: Success modal
  })
  onError((err) => {})
}

const title = computed(() => `${t('navigation.projects')}`)
usePageTitle(title)
</script>
<template>
  <CrdWrapper v-if="!isLoading">
    <div class="sm:flex sm:items-center">
      <div class="flex justify-end gap-x-4 w-full">
        <div class="w-max">
          <button
            type="button"
            class="inline-flex items-center w-full mr-2 gap-x-1.5 rounded-md bg-indigo-600 px-2.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            @click="onAddProjectsButtonClicked"
          >
            <UserPlusIcon class="-ml-0.5 size-5" aria-hidden="true" />
            {{ t('projects.page.addProject') }}
          </button>
        </div>
      </div>
    </div>
    <ul role="list" class="divide-y divide-gray-100 w-full space-y-2">
      <CrdBlock
        v-for="project in projects ?? []"
        :key="project.id as number"
        :custom-background="true"
        :class-custom="{
          'justify-between h-20': true,
          [projectSelected?.id === project.id ? 'bg-purple-50' : 'bg-white']: true,
        }"
      >
        <div class="min-w-0">
          <div class="flex items-start gap-x-3">
            <p class="text-sm/6 font-semibold text-gray-900">{{ project.title }}</p>
            <p
              :class="[
                getProjectBadgeStylesByStatus(getProjectStatus(project)),
                'mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset',
              ]"
            >
              {{ getProjectBadgeLabelByStatus(getProjectStatus(project)) }}
            </p>
          </div>
          <div class="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
            <p class="whitespace-nowrap">
              {{ t('projects.page.createdAt') }}
              <time :datetime="formatDateToDDMMYYYYHHmm(project!.createdBy!.date)">
                <span class="font-medium">{{
                  formatDateToDDMMYYYYHHmm(project!.createdBy!.date)
                }}</span>
              </time>
            </p>
            <svg viewBox="0 0 2 2" class="size-0.5 fill-current">
              <circle cx="1" cy="1" r="1" />
            </svg>
            <p class="truncate">
              {{ t('projects.page.createdBy') }} {{ project?.createdBy?.firstname }}
              {{ project?.createdBy?.lastname }}
            </p>
            <svg viewBox="0 0 2 2" class="size-0.5 fill-current">
              <circle cx="1" cy="1" r="1" />
            </svg>
            <p class="truncate">{{ t('projects.page.totalChats') }}: {{ project.chatsAmount }}</p>
            <svg viewBox="0 0 2 2" class="size-0.5 fill-current">
              <circle cx="1" cy="1" r="1" />
            </svg>
            <p class="truncate">{{ t('projects.page.activeChats') }}: N/A</p>
            <svg viewBox="0 0 2 2" class="size-0.5 fill-current">
              <circle cx="1" cy="1" r="1" />
            </svg>
          </div>
        </div>
        <div class="flex flex-col w-36 items-end items-center justify-center">
        <div class="flex flex-none items-center gap-x-4">
          <RouterLink
            :to="`/projects/${project.id}/edit`"
            class="hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block"
          >
            {{ t('common.buttons.edit') }}
          </RouterLink>
          <Menu as="div" class="relative flex-none">
            <MenuButton class="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
              <span class="sr-only">{{ t('projects.page.openOptions') }}</span>
              <EllipsisVerticalIcon class="size-5" aria-hidden="true" />
            </MenuButton>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
              >
                <MenuItem
                  v-if="isNull(project.pausedBy)"
                  v-slot="{ active }"
                  @click="
                    changeProjectState(project.id as number, ChangeProjectStateType.Deactivate)
                  "
                >
                  <span
                    :class="[
                      active ? 'bg-gray-50 outline-none hover:cursor-pointer' : '',
                      'block px-3 py-1 text-sm/6 text-gray-900',
                    ]"
                  >
                    {{ t('projects.page.deactivate') }}
                  </span>
                </MenuItem>
                <MenuItem
                  v-else
                  v-slot="{ active }"
                  @click="changeProjectState(project.id as number, ChangeProjectStateType.Activate)"
                >
                  <span
                    :class="[
                      active ? 'bg-gray-50 outline-none hover:cursor-pointer' : '',
                      'block px-3 py-1 text-sm/6 text-gray-900',
                    ]"
                  >
                    {{ t('projects.page.activate') }}
                  </span>
                </MenuItem>
                <MenuItem
                  v-if="isNull(project.archivedBy)"
                  v-slot="{ active }"
                  @click="changeProjectState(project.id as number, ChangeProjectStateType.Archive)"
                >
                  <span
                    :class="[
                      active ? 'bg-gray-50 outline-none hover:cursor-pointer' : '',
                      'block px-3 py-1 text-sm/6 text-gray-900',
                    ]"
                  >
                    {{ t('projects.page.archive') }}
                  </span>
                </MenuItem>
                <MenuItem
                  v-else
                  v-slot="{ active }"
                  @click="
                    changeProjectState(project.id as number, ChangeProjectStateType.UnAarchive)
                  "
                >
                  <span
                    :class="[
                      active ? 'bg-gray-50 outline-none hover:cursor-pointer' : '',
                      'block px-3 py-1 text-sm/6 text-gray-900',
                    ]"
                  >
                    {{ t('projects.page.unarchive') }}
                  </span>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
        </div>
      </CrdBlock>
    </ul>
    <router-view />
  </CrdWrapper>
  <div class="pre-footer-text">
    {{ t('projects.page.overviewDescription') }}
  </div>
</template>
<style scoped>
.blinking {
  animation: blink 2s infinite;
}

@keyframes blink {
  0% {
    opacity: 0.1;
  }
  40% {
    opacity: 1;
  }
  100% {
    opacity: 0.1;
  }
}
</style>
